import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  sendVerificationCode,
  updateCustomerEmail,
  verifyCode,
  verifyCodeReset,
} from '../../../../actions';
import { CERTIFICATE_LINKING_STEPS } from '../../petDemographics.constants';
import { useResendCodeTimer } from './useResendCodeTimer';
import {
  OTP_VERIFICATION_TYPE,
  SERVICE_NAME,
} from '../../../common/otp/otp.constants';

const useAuthorizationCode = ({
  email = '',
  setCurrentStep = () => { },
  certificateNumber = '',
}) => {
  const verificationState = useSelector(({
    otpVerification,
  }) => (otpVerification.verificationCode), shallowEqual);
  const updateCustomerEmailState = useSelector(({
    petDemographics,
  }) => (petDemographics.updateCustomerEmail), shallowEqual);
  const [authCode, setAuthCode] = useState('');
  const [codeReSent, setCodeResent] = useState(false);
  const dispatch = useDispatch();
  const { remainingSeconds, startTimer } = useResendCodeTimer();

  const doSendVerificationCode = useCallback(() => {
    dispatch(sendVerificationCode({
      email,
      // always PPI product for futurama
      serviceName: SERVICE_NAME.PPI,
      // always as email type (as specified) for futurama
      verificationType: OTP_VERIFICATION_TYPE.EMAIL,
    }));
  }, [email, dispatch]);

  useEffect(() => {
    doSendVerificationCode();
  }, [doSendVerificationCode]);

  const handleInputChange = useCallback((event) => {
    setAuthCode(event.target.value);
    if (verificationState.error) {
      dispatch(verifyCodeReset());
    }
  }, [verificationState]);

  useEffect(() => {
    if (!updateCustomerEmailState.loading && updateCustomerEmailState.updated) {
      // goes to email updated success screen :mailbox:
      setCurrentStep(CERTIFICATE_LINKING_STEPS.EMAIL_UPDATED_SUCCESS);
    }
  }, [updateCustomerEmailState]);

  useEffect(() => {
    // success validation code scenario :whale:
    if (!verificationState.loading && verificationState.verified) {
      // then we update the customer email
      dispatch(updateCustomerEmail({ certificateNumber, email }));
    }
  }, [certificateNumber, dispatch, email, verificationState]);

  const doVerifyCode = useCallback(() => {
    dispatch(verifyCodeReset());

    dispatch(verifyCode({
      code: authCode,
      email,
      // always as email type (as specified) for futurama
      useEmailVerification: OTP_VERIFICATION_TYPE.EMAIL,
    }));
  }, [authCode, dispatch, email]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    doVerifyCode();
  }, [doVerifyCode]);

  const handleResendCode = useCallback(() => {
    startTimer();
    setCodeResent(true);
    doSendVerificationCode();
  }, [startTimer, doSendVerificationCode]);

  useEffect(() => {
    startTimer();
  }, []);

  return {
    authCode,
    codeReSent: codeReSent && verificationState.success,
    error: verificationState.error || updateCustomerEmailState.error,
    handleInputChange,
    handleResendCode,
    handleSubmit,
    isLoading: verificationState.isLoading || updateCustomerEmailState.loading,
    remainingSeconds,
  };
};

export { useAuthorizationCode };
